import { Injectable } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, Observable, Subject, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, pairwise, switchMap, take, timeout } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TaskConversationState, TaskState, ITask } from '@nimbus-assistant/models/task.model';
import {
  TaskFlowDetails,
  CallerInfo,
  CallDirection,
  TaskFlowParticipantType,
  Channel,
  Modality,
  TaskParametersResponse,
} from '@nimbus-assistant/models/task-types.model';
import { TaskAttributes } from '@nimbus-assistant/models/task-attributes.model';
import { TeamInfo } from '@nimbus-assistant/models/team-info';
import { ConversationInfo } from '@nimbus-assistant/models/incoming-call-info';
import { OpenInBrowserMessage } from '@nimbus-assistant/models/web-view-messages/open-in-browser-message';
import { TeamInfoLinksToOpen } from '@nimbus-assistant/models/team-info-links-to-open';
import { CallEvent } from '@nimbus-assistant/enums/call-event';
import { resolveActiveTask, isAcwState, isUnifyTask } from '@nimbus-assistant/helpers/task-priority.helpers';
import { UserState } from '@nimbus-library/features/user-state/models/user-state';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { INestedCustomerParameters } from '@nimbus-global/features/reportingLiveData/models/session-parameters.model';
import { IUserSessionLive, UserSessionState } from '@nimbus-global/features/reportingLiveData/models/user-session.model';
import { CallerType } from '@nimbus-library/features/shared/models/enum/caller-type.enum';
import { AssistantHub } from './signalr/assistant.hub';
import { ApiService } from './api/api.service';
import { LoggingService } from './logging.service';
import { SettingsService } from './settings.service';
import { TriggerService } from './trigger.service';
import { MessageService } from './message.service';
import { AuthService } from './auth.service';
import { LocalStorageService, localStorageKeys } from './local-storage.service';
import { ServicesService } from './services.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class AssistantTasksService {
  private static readonly CUSTOMER_CONTEXT_FIELDS: { field: keyof INestedCustomerParameters; key: TranslationKeys }[] = [
    { field: 'email', key: TranslationKeys.AssistantContextEmail },
    { field: 'imAddress', key: TranslationKeys.AssistantContextImAddress },
  ];

  /** Upper bound the CallAccepted trigger waits for refreshed context parameters before using the cached ones. */
  private static readonly ACCEPT_CONTEXT_PARAMS_TIMEOUT_MS = 3000;
  private static readonly DURATION_PARTS_COUNT = 3;
  private static readonly SECONDS_PER_HOUR = 3600;
  private static readonly SECONDS_PER_MINUTE = 60;

  private readonly _tasks$ = new BehaviorSubject<ITask[]>([]);
  private readonly _directCallTask$ = new BehaviorSubject<ITask | null>(null);
  private readonly _rona$ = new BehaviorSubject<{ started: string } | null>(null);
  private readonly _fullReload$ = new Subject<string>();
  private readonly _codesLoading = new Set<string>();
  private readonly _detailsLoaded = new Set<string>();
  private readonly _contextParamsGuard = new Set<string>();
  /** Conversations whose context parameters were refreshed at accept - older ringing-time responses must not overwrite them. */
  private readonly _acceptContextParamsApplied = new Set<string>();
  private readonly _callFlowLoaded = new Set<string>();
  private readonly _callFlowFetchAttempted = new Set<string>();
  private readonly _callFlowLoading$ = new BehaviorSubject<Set<string>>(new Set());
  private readonly _contextParamsLoading$ = new BehaviorSubject<Set<string>>(new Set());
  private readonly _terminatedOrderNumbers = new Map<string, number>();

  public readonly tasks$: Observable<ITask[]> = combineLatest([this._tasks$, this._directCallTask$]).pipe(
    map(([serviceTasks, directCall]) => (directCall ? [...serviceTasks, directCall] : serviceTasks)),
  );

  public readonly connectedTask$: Observable<ITask | null> = this.tasks$.pipe(
    map((tasks) => tasks.find((t) => t.state === TaskState.Connected) ?? null),
    distinctUntilChanged(
      (a, b) =>
        a?.unifiedConversationId === b?.unifiedConversationId &&
        a?.conversationState === b?.conversationState &&
        a?.consultationSession?.currentState === b?.consultationSession?.currentState &&
        a?.consultationSession?.targetState?.state === b?.consultationSession?.targetState?.state &&
        a?.callerHungUpAt === b?.callerHungUpAt,
    ),
  );

  public readonly incomingTask$: Observable<ITask | null> = this.tasks$.pipe(
    map((tasks) => tasks.find((t) => t.state === TaskState.Ringing) ?? null),
    // A transfer can hand the same unifiedConversationId a new conversationId/userSessionId while
    // staying Ringing, with no guaranteed null gap in between - compare those too so
    // loadContextParameters/loadCallFlow (keyed on them) re-fire with the current leg's ids
    // instead of silently keeping the old, now-invalid ones.
    distinctUntilChanged(
      (a, b) =>
        a?.unifiedConversationId === b?.unifiedConversationId &&
        a?.conversationId === b?.conversationId &&
        a?.userSessionId === b?.userSessionId,
    ),
  );

  public readonly parkedTasks$: Observable<ITask[]> = this.tasks$.pipe(
    map((tasks) => tasks.filter((t) => t.state === TaskState.Parked)),
    distinctUntilChanged((a, b) => a.length === b.length && a.every((t, i) => t.unifiedConversationId === b[i].unifiedConversationId)),
  );

  public readonly acwTask$: Observable<ITask | null> = this.tasks$.pipe(
    map((tasks) => tasks.find((t) => isAcwState(t.state)) ?? null),
    distinctUntilChanged((a, b) => a?.unifiedConversationId === b?.unifiedConversationId && a?.state === b?.state),
  );

  public readonly rona$ = this._rona$.asObservable();

  public readonly isAudioCall$: Observable<boolean> = this.connectedTask$.pipe(
    map((t) => t?.modality === Modality.Audio),
    distinctUntilChanged(),
  );
  public readonly inCall$: Observable<boolean> = this.connectedTask$.pipe(
    map((t) => !!t),
    distinctUntilChanged(),
  );
  public readonly isInAcw$: Observable<boolean> = this.acwTask$.pipe(
    map((t) => !!t),
    distinctUntilChanged(),
  );

  /** The most relevant active task: Connected > ACW > Incoming > null */
  public readonly activeTask$: Observable<ITask | null> = combineLatest([this.connectedTask$, this.acwTask$, this.incomingTask$]).pipe(
    map(([connected, acw, incoming]) => resolveActiveTask(connected, acw, incoming)),
  );

  public readonly contextParamsLoading$ = this._contextParamsLoading$.asObservable();
  public readonly callFlowLoading$ = this._callFlowLoading$.asObservable();

  public get directCallTaskSnapshot(): ITask | null {
    return this._directCallTask$.getValue();
  }

  public get connectedTaskSnapshot(): ITask | null {
    const directCall = this._directCallTask$.getValue();
    if (directCall?.state === TaskState.Connected) {
      return directCall;
    }
    return this._tasks$.getValue().find((t) => t.state === TaskState.Connected) ?? null;
  }

  public get acwTaskSnapshot(): ITask | null {
    return this._tasks$.getValue().find((t) => isAcwState(t.state)) ?? null;
  }

  public constructor(
    private readonly hub: AssistantHub,
    private readonly apiService: ApiService,
    private readonly loggingService: LoggingService,
    private readonly settingsService: SettingsService,
    private readonly triggerService: TriggerService,
    private readonly authService: AuthService,
    private readonly localStorageService: LocalStorageService,
    private readonly servicesService: ServicesService,
  ) {
    this._fullReload$
      .pipe(
        switchMap((userId) => this.loadTasks(userId)),
        untilDestroyed(this),
      )
      .subscribe();
    this.subscribeToSignalR();
    this.subscribeToConnectionState();
    this.subscribeToTaskTransitions();
  }

  public isDetailsLoaded(unifiedConversationId: string): boolean {
    return this._detailsLoaded.has(unifiedConversationId);
  }

  public acceptTask(callId: string, sessionId: string, modality: Modality): Observable<void> {
    return this.apiService.acceptSession(callId, sessionId, modality).pipe(
      catchError((error) => {
        this.loggingService.Error(`AssistantTasksService accept error: ${JSON.stringify(error)}`);
        return of(null);
      }),
      take(1),
    );
  }

  public declineTask(callId: string, sessionId: string, modality: Modality): Observable<void> {
    return this.apiService.declineSession(callId, sessionId, modality).pipe(
      catchError((error) => {
        this.loggingService.Error(`AssistantTasksService decline error: ${JSON.stringify(error)}`);
        return of(null);
      }),
      take(1),
    );
  }

  public parkTask(callId: string): Observable<void> {
    const user365Id = this.settingsService.settings$.getValue()?.office365Id;
    return this.apiService.parkSession(callId, user365Id).pipe(
      catchError((error) => {
        this.loggingService.Error(`AssistantTasksService park error: ${JSON.stringify(error)}`);
        return of(null);
      }),
      take(1),
    );
  }

  public terminateTask(callId: string, sessionId: string, modality: Modality): Observable<void> {
    return this.apiService.terminateSession(callId, sessionId, modality).pipe(
      catchError((error) => {
        this.loggingService.Error(`AssistantTasksService terminate error: ${JSON.stringify(error)}`);
        return of(null);
      }),
      take(1),
    );
  }

  // --- Internal ---

  /** Inject a synthetic direct-call task (ACS P2P). Has no effect if a task already exists. */
  public addDirectCallTask(task: ITask): void {
    if (this._directCallTask$.getValue()) {
      this.loggingService.Warning(`AssistantTasksService: addDirectCallTask ignored — already have direct call`);
      return;
    }
    this.loggingService.Info(`AssistantTasksService: addDirectCallTask ${task.unifiedConversationId}`);
    this._directCallTask$.next(task);
  }

  /** Remove the synthetic direct-call task. */
  public removeDirectCallTask(acsCallId: string): void {
    if (this._directCallTask$.getValue()?.unifiedConversationId === acsCallId) {
      this.loggingService.Info(`AssistantTasksService: removeDirectCallTask ${acsCallId}`);
      this._directCallTask$.next(null);
    }
  }

  /** Patch fields on the existing direct-call task. */
  public updateDirectCallTask(acsCallId: string, patch: Partial<ITask>): void {
    const current = this._directCallTask$.getValue();
    if (current?.unifiedConversationId !== acsCallId) {
      return;
    }
    this.loggingService.Info(`AssistantTasksService: updateDirectCallTask ${acsCallId}, keys: ${Object.keys(patch).join(', ')}`);
    this._directCallTask$.next({ ...current, ...patch });
  }

  /**
   * Re-read the codes and tags of a Unify call. NimbusOne owns them there, no live-data message
   * carries their values (IUserSessionLive only says whether the widget is enabled), and loadCodes()
   * runs once per leg - so without this the Assistant would show the first response for the whole
   * leg. Called when the codes tab is opened and on every session update while it is open.
   */
  public refreshCodes(task: ITask): void {
    const id = task?.unifiedConversationId;
    // Not loaded yet means loadCodes() is still to run (or is running) and will bring the values in.
    if (!id || !this._detailsLoaded.has(id) || this._codesLoading.has(id)) {
      return;
    }
    this._codesLoading.add(id);

    this.apiService
      .getCodes(task.teamId, task.conversationId, task.userSessionId)
      .pipe(
        catchError((error) => {
          this.loggingService.Error(`AssistantTasksService refreshCodes error: ${JSON.stringify(error)}`);
          return of(null);
        }),
        untilDestroyed(this),
      )
      .subscribe((codes) => {
        this._codesLoading.delete(id);
        const current = this._tasks$.getValue();
        const idx = current.findIndex((t) => t.unifiedConversationId === id);
        // Ignore a failed call and a response from a superseded leg (a transfer changed the userSessionId).
        if (!codes || idx < 0 || current[idx].userSessionId !== task.userSessionId) {
          return;
        }
        // Every emission rebuilds the codes form, so only publish when the values actually changed.
        if (JSON.stringify(current[idx].conversationCodes) === JSON.stringify(codes)) {
          return;
        }
        const updated = [...current];
        updated[idx] = { ...updated[idx], conversationCodes: codes };
        this._tasks$.next(updated);
      });
  }

  private subscribeToSignalR(): void {
    this.hub.onLiveDataUserSessionChanged$.pipe(untilDestroyed(this)).subscribe((session) => {
      this.loggingService.Info(
        `AssistantTasksService received LiveDataUserSessionChanged: ${session?.unifiedConversationId} → ${session?.state}`,
      );
      if (session) {
        this.onLiveDataUserSessionChanged(session);
      }
    });

    this.hub.availabilityState$
      .pipe(
        filter((a) => !!a),
        untilDestroyed(this),
      )
      .subscribe((availability) => {
        if (availability.userStateType === UserState.Rona) {
          this.loggingService.Info(`AssistantTasksService: RONA from availabilityState, ronaStartedAt: ${availability.ronaStartedAt}`);
          if (!this._rona$.value) {
            this._rona$.next({ started: availability.ronaStartedAt ?? moment().toISOString() });
          }
        } else if (this._rona$.value) {
          this.loggingService.Info(`AssistantTasksService: clearing RONA (userStateType: ${availability.userStateType})`);
          this._rona$.next(null);
        }
      });
  }

  private onLiveDataUserSessionChanged(session: IUserSessionLive): void {
    const { unifiedConversationId: id, conversationId, orderNumber } = session;
    const current = this._tasks$.getValue();
    const idx = current.findIndex((t) => t.unifiedConversationId === id);
    const existing = idx >= 0 ? current[idx] : null;

    // orderNumber only increases within a single conversationId - a transfer keeps the same
    // unifiedConversationId (same displayed task) but starts a new conversationId with its own
    // orderNumber sequence, so a stale high orderNumber from the previous leg must not block it.
    const existingOrderNumber =
      existing?.conversationId === conversationId ? existing.orderNumber : this._terminatedOrderNumbers.get(conversationId);
    if (existingOrderNumber !== undefined && orderNumber <= existingOrderNumber) {
      this.loggingService.Info(
        `AssistantTasksService: ignoring out-of-order message ${id}/${conversationId} (incoming ${orderNumber} <= existing ${existingOrderNumber})`,
      );
      return;
    }

    if (session.state === UserSessionState.Terminated) {
      this._terminatedOrderNumbers.set(conversationId, orderNumber);
      this._contextParamsGuard.delete(id);
      this._acceptContextParamsApplied.delete(id);
      this._detailsLoaded.delete(id);
      this._callFlowLoaded.delete(id);
      this._callFlowFetchAttempted.delete(id);
      this.setCallFlowLoading(id, false);
      this._codesLoading.delete(id);
      this.setContextParamsLoading(id, false);
      if (idx >= 0) {
        const terminatedTask = current[idx];
        if (!terminatedTask.isSupervision) {
          this.triggerService.processCallEvent(CallEvent.TaskTerminated, this.mapToConversationInfo(terminatedTask));
        }
        this.loggingService.Info(`AssistantTasksService: removing task ${id} (Terminated, orderNumber ${orderNumber})`);
        this._tasks$.next(current.filter((t) => t.unifiedConversationId !== id));
      }
      return;
    }

    const task = this.mapSessionToTask(session);
    // A transfer reuses the same unifiedConversationId but starts a new conversationId/userSessionId.
    // Every enrichment guard and the already-fetched data below are keyed on the previous leg, so they
    // must be dropped when the leg changes - otherwise loadContextParameters/loadCallFlow early-return
    // and the new session never re-enriches, while the old (now 404) leg's data stays on screen.
    const legChanged = !!existing && (existing.userSessionId !== task.userSessionId || existing.conversationId !== task.conversationId);
    if (legChanged) {
      this._contextParamsGuard.delete(id);
      this._acceptContextParamsApplied.delete(id);
      this._detailsLoaded.delete(id);
      this._callFlowLoaded.delete(id);
      this._callFlowFetchAttempted.delete(id);
      this._codesLoading.delete(id);
      this.setCallFlowLoading(id, false);
      this.setContextParamsLoading(id, false);
    }
    if (existing && !legChanged) {
      task.contextData = existing.contextData;
      task.resolvedParameters = existing.resolvedParameters;
    }
    if (existing && this._detailsLoaded.has(id)) {
      task.conversationCodes = existing.conversationCodes;
      task.agentChatDeepLink = existing.agentChatDeepLink;
    }
    // mapSessionToTask only rebuilds the caller + first-service placeholder, so once the full journey
    // has been fetched it must survive every subsequent session update (e.g. Connected → ACW).
    if (existing && this._callFlowLoaded.has(id)) {
      task.callFlow = existing.callFlow;
    }
    // Show the loading state until the journey has been fetched at least once, so the tab renders a
    // spinner first and then the full flow (or the placeholder as fallback) - never placeholder-first.
    if (task.isServiceCall && this.isCallFlowFetchState(task) && !this._callFlowFetchAttempted.has(id)) {
      this.setCallFlowLoading(id, true);
    }
    const updated = idx >= 0 ? [...current.slice(0, idx), task, ...current.slice(idx + 1)] : [...current, task];
    this.loggingService.Info(`AssistantTasksService: upserted task ${id} (${task.state}, orderNumber ${orderNumber})`);
    this._tasks$.next(updated);

    // The codes above are the ones fetched once when the task connected. NimbusOne owns them on a
    // Unify call and can change them at any time, so that first response goes stale - re-read it
    // whenever the session changes instead of showing it for the rest of the leg.
    if (isUnifyTask(task)) {
      this.refreshCodes(task);
    }
  }

  private subscribeToConnectionState(): void {
    combineLatest([
      this.hub.isConnected$.pipe(distinctUntilChanged()),
      this.settingsService.settings$.pipe(
        map((s) => s?.office365Id),
        filter((id) => !!id),
        distinctUntilChanged(),
      ),
    ])
      .pipe(
        filter(([connected]) => connected),
        untilDestroyed(this),
      )
      .subscribe(([_, userId]) => this._fullReload$.next(userId));

    this.hub.isConnected$
      .pipe(
        distinctUntilChanged(),
        filter((connected) => !connected),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this._tasks$.next([]);
        this._terminatedOrderNumbers.clear();
        this._detailsLoaded.clear();
        this._callFlowLoaded.clear();
        this._callFlowFetchAttempted.clear();
        this._callFlowLoading$.next(new Set());
        this._contextParamsGuard.clear();
        this._acceptContextParamsApplied.clear();
        this._contextParamsLoading$.next(new Set());
      });
  }

  private loadTasks(userId: string): Observable<ITask[]> {
    return this.apiService.getUserSessionsById(userId).pipe(
      catchError((error) => {
        this.loggingService.Error(`AssistantTasksService loadTasks failed: ${JSON.stringify(error)}`);
        return of(null);
      }),
      map((loadedSessions) => {
        const sessions = loadedSessions ?? [];
        const existing = this._tasks$.getValue();
        const tasks = sessions.map((s) => {
          const task = this.mapSessionToTask(s);
          const prev = existing.find((t) => t.unifiedConversationId === task.unifiedConversationId);
          if (prev && this._detailsLoaded.has(task.unifiedConversationId)) {
            task.conversationCodes = prev.conversationCodes;
            task.agentChatDeepLink = prev.agentChatDeepLink;
          }
          if (prev && this._callFlowLoaded.has(task.unifiedConversationId)) {
            task.callFlow = prev.callFlow;
          }
          if (task.isServiceCall && this.isCallFlowFetchState(task) && !this._callFlowFetchAttempted.has(task.unifiedConversationId)) {
            this.setCallFlowLoading(task.unifiedConversationId, true);
          }
          return task;
        });
        this._tasks$.next(tasks);
        if (loadedSessions) {
          this.reconcileStoredCallAttributes(tasks);
        }
        return tasks;
      }),
    );
  }

  /**
   * Called with an authoritative REST snapshot (i.e. after a successful (re)connect load). An entry
   * kept in localStorage for a conversation the snapshot no longer reports belongs to a call the
   * agent can no longer work on, so it is dropped here instead of being left behind for a later one.
   *
   * Deliberately does not submit: a missing conversation is not an end-of-call signal, only the
   * absence of an active session. Auto-submit stays tied to an explicit Terminated message, so
   * attributes of unknown age are never pushed to the backend.
   */
  private reconcileStoredCallAttributes(tasks: ITask[]): void {
    const stored: TaskAttributes = this.localStorageService.getData(localStorageKeys.callAttributes);
    if (!stored?.conversationId) {
      return;
    }

    if (tasks.some((t) => t.conversationId === stored.conversationId)) {
      return;
    }

    this.loggingService.Info(
      `AssistantTasksService: discarding pending call attributes of ${stored.conversationId} - the conversation is not in the snapshot.`,
    );
    this.localStorageService.clearData(localStorageKeys.callAttributes);
  }

  private subscribeToTaskTransitions(): void {
    // Auto-submit call attributes when a task disappears from the list
    this.tasks$.pipe(pairwise(), untilDestroyed(this)).subscribe(([prev, curr]) => {
      const removedTasks = prev.filter((t) => !curr.find((c) => c.unifiedConversationId === t.unifiedConversationId));
      if (!removedTasks.length) {
        return;
      }

      // A removal is only authoritative while the hub is up: it then originates from a Terminated
      // live-data message. With the hub down the list was emptied by subscribeToConnectionState(),
      // which says nothing about the tasks themselves - the backend may well still hold them, and
      // loadTasks() will bring them back on reconnect. Submitting (or discarding) call attributes
      // here would throw away what the agent typed during an ACW that has not ended.
      if (!this.hub.isConnected$.value) {
        this.loggingService.Info(
          `AssistantTasksService: skipping call attributes auto-submit for ${removedTasks.length} task(s) - hub disconnected, removal is not authoritative.`,
        );
        return;
      }

      removedTasks.forEach((task) => this.submitCallAttributesForTask(task));
    });

    // Load codes when a service task becomes Connected or ACW
    this.activeTask$
      .pipe(
        filter(
          (task) =>
            !!task && task.isServiceCall && task.state !== TaskState.Ringing && !this._detailsLoaded.has(task.unifiedConversationId),
        ),
        untilDestroyed(this),
      )
      .subscribe((task) => {
        this.loadCodes(task);
      });

    this.incomingTask$
      .pipe(
        filter((task) => !!task && task.isServiceCall),
        untilDestroyed(this),
      )
      .subscribe((task) => this.loadContextParameters(task));

    // Open service context links on incoming task (Ringing).
    // Trigger firing for service calls is deferred to loadContextParameters so resolvedParameters
    // are available for placeholder resolution. Direct (non-service) call triggers are owned by
    // TaskDirectCallService, which fires them with a fully resolved IncomingCallInfo (AB#154857).
    this.incomingTask$
      .pipe(
        filter((task) => !!task && !task.isSupervision),
        untilDestroyed(this),
      )
      .subscribe((task) => {
        this.openServiceContextLinks(task);
      });

    // Fire triggers on service task accepted (Ringing → Connected).
    // Direct (non-service) call accept triggers are owned by TaskDirectCallService (AB#154857).
    this.connectedTask$
      .pipe(
        distinctUntilChanged((a, b) => a?.unifiedConversationId === b?.unifiedConversationId),
        filter((task) => !!task && !task.isSupervision && task.isServiceCall),
        untilDestroyed(this),
      )
      .subscribe((task) => this.fireCallAcceptedTrigger(task));
  }

  /**
   * Re-fetches the context parameters before firing the CallAccepted trigger, as they may have been
   * updated after the ringing-time fetch - e.g. by a Power Automate "Update task" while an outbound
   * call on behalf of a service is ringing (AB#158359). On failure/timeout falls back to the latest
   * cached parameters of the same leg (a ringing-time response may have landed in the meantime).
   */
  private fireCallAcceptedTrigger(task: ITask): void {
    this.apiService
      .getContextParameters(task.teamId, task.conversationId, task.userSessionId)
      .pipe(
        timeout(AssistantTasksService.ACCEPT_CONTEXT_PARAMS_TIMEOUT_MS),
        catchError((error) => {
          this.loggingService.Error(`AssistantTasksService fireCallAcceptedTrigger error: ${JSON.stringify(error)}`);
          return of(null);
        }),
        untilDestroyed(this),
      )
      .subscribe((result) => {
        const updatedTask = result ? this.applyContextParamsResult(task, result) : this.findSameLegTask(task);
        if (result && updatedTask) {
          this._acceptContextParamsApplied.add(task.unifiedConversationId);
        }
        this.triggerService.processCallEvent(CallEvent.CallAccepted, this.mapToConversationInfo(updatedTask ?? task));
      });
  }

  private loadCodes(task: ITask): void {
    if (this._codesLoading.has(task.unifiedConversationId)) {
      return;
    }
    this._codesLoading.add(task.unifiedConversationId);

    const codes$ = this.apiService.getCodes(task.teamId, task.conversationId, task.userSessionId).pipe(
      catchError((error) => {
        this.loggingService.Error(`AssistantTasksService loadCodes error: ${JSON.stringify(error)}`);
        return of(null);
      }),
    );
    const isTwilioChannel = [Channel.TwilioWhatsapp, Channel.TwilioSms, Channel.TwilioRcs].includes(task.channel);
    const chatDeepLink$ =
      task.modality === Modality.IM && !isTwilioChannel
        ? this.apiService.getChatDeepLink(task.userSessionId).pipe(
            catchError((error) => {
              this.loggingService.Error(`AssistantTasksService getChatDeepLink error: ${JSON.stringify(error)}`);
              return of('');
            }),
          )
        : of('');
    const alreadyLoadedContextParams = this._contextParamsGuard.has(task.unifiedConversationId);
    const contextParams$ = alreadyLoadedContextParams
      ? of(null)
      : this.apiService.getContextParameters(task.teamId, task.conversationId, task.userSessionId).pipe(
          catchError((error) => {
            this.loggingService.Error(`AssistantTasksService loadContextParameters error: ${JSON.stringify(error)}`);
            return of(null);
          }),
        );
    this._contextParamsGuard.add(task.unifiedConversationId);

    const callFlow$ = this.apiService.getCallFlow(task.teamId, task.conversationId, task.userSessionId).pipe(
      catchError((error) => {
        this.loggingService.Error(`AssistantTasksService loadCallFlow error: ${JSON.stringify(error)}`);
        return of(null);
      }),
    );

    forkJoin([codes$, chatDeepLink$, contextParams$, callFlow$])
      .pipe(untilDestroyed(this))
      .subscribe(([codes, agentChatDeepLink, contextParamsResult, callFlow]) => {
        const current = this._tasks$.getValue();
        const idx = current.findIndex((t) => t.unifiedConversationId === task.unifiedConversationId);
        // Ignore a response from a superseded leg (a transfer changed the userSessionId) so it never
        // clears the new leg's loading state nor overwrites its data.
        if (idx < 0 || current[idx].userSessionId !== task.userSessionId) {
          return;
        }
        this._codesLoading.delete(task.unifiedConversationId);

        const namedEntries = this.buildNamedContextEntries(contextParamsResult, current[idx].customContextParameters);
        const namedKeys = new Set(namedEntries.map((e) => e.key));
        const existingContextData = (current[idx].contextData ?? []).filter((e) => !namedKeys.has(e.key));
        this._detailsLoaded.add(task.unifiedConversationId);
        this._callFlowFetchAttempted.add(task.unifiedConversationId);
        this.setCallFlowLoading(task.unifiedConversationId, false);
        if (callFlow?.length) {
          this._callFlowLoaded.add(task.unifiedConversationId);
        }
        const updated = [...current];
        updated[idx] = {
          ...updated[idx],
          conversationCodes: codes,
          agentChatDeepLink: agentChatDeepLink || undefined,
          contextData: [...existingContextData, ...namedEntries],
          resolvedParameters: contextParamsResult?.templateParameters ?? current[idx].resolvedParameters,
          callFlow: callFlow?.length ? callFlow : updated[idx].callFlow,
        };
        this._tasks$.next(updated);
      });
  }

  private loadContextParameters(task: ITask): void {
    if (this._contextParamsGuard.has(task.unifiedConversationId)) {
      return;
    }
    this._contextParamsGuard.add(task.unifiedConversationId);
    this.setContextParamsLoading(task.unifiedConversationId, true);

    // Fetch the full call journey already while ringing so the pre-call info shows transfers/IVRs
    // instead of the caller + first-service placeholder. Kept as a separate request/subscription so
    // the IncomingCall trigger (screen pop) below is not delayed by call-flow endpoint latency - it
    // only needs resolvedParameters from the context-parameters response.
    this.loadCallFlow(task);

    this.apiService
      .getContextParameters(task.teamId, task.conversationId, task.userSessionId)
      .pipe(
        catchError((error) => {
          this.loggingService.Error(`AssistantTasksService loadContextParameters error: ${JSON.stringify(error)}`);
          return of(null);
        }),
        untilDestroyed(this),
      )
      .subscribe((result) => {
        // A newer accept-time refresh already patched this leg - keep it, but still fire IncomingCall.
        const updatedTask = this._acceptContextParamsApplied.has(task.unifiedConversationId)
          ? this.findSameLegTask(task)
          : this.applyContextParamsResult(task, result);
        if (!updatedTask) {
          return;
        }
        this.setContextParamsLoading(task.unifiedConversationId, false);

        // Fire IncomingCall trigger now that resolvedParameters are available for placeholder resolution
        if (!updatedTask.isSupervision) {
          this.triggerService.processCallEvent(CallEvent.IncomingCall, this.mapToConversationInfo(updatedTask));
        }
      });
  }

  private findSameLegTask(task: ITask): ITask | null {
    return (
      this._tasks$
        .getValue()
        .find((t) => t.unifiedConversationId === task.unifiedConversationId && t.userSessionId === task.userSessionId) ?? null
    );
  }

  /**
   * Patches a context-parameters response (named context entries + resolvedParameters) into the task.
   * Returns the updated task, or null when the task is gone or the response is from a superseded leg
   * (a transfer changed the userSessionId).
   */
  private applyContextParamsResult(task: ITask, result: TaskParametersResponse | null): ITask | null {
    const current = this._tasks$.getValue();
    const idx = current.findIndex((t) => t.unifiedConversationId === task.unifiedConversationId);
    if (idx < 0 || current[idx].userSessionId !== task.userSessionId) {
      return null;
    }
    const namedEntries = this.buildNamedContextEntries(result, current[idx].customContextParameters);
    const namedKeys = new Set(namedEntries.map((e) => e.key));
    const existingContextData = (current[idx].contextData ?? []).filter((e) => !namedKeys.has(e.key));
    const updated = [...current];
    updated[idx] = {
      ...updated[idx],
      contextData: [...existingContextData, ...namedEntries],
      resolvedParameters: result?.templateParameters ?? undefined,
    };
    this._tasks$.next(updated);
    return updated[idx];
  }

  /** Fetches the full call journey for a ringing task and patches it independently of other enrichment. */
  private loadCallFlow(task: ITask): void {
    this.apiService
      .getCallFlow(task.teamId, task.conversationId, task.userSessionId)
      .pipe(
        catchError((error) => {
          this.loggingService.Error(`AssistantTasksService loadCallFlow (ringing) error: ${JSON.stringify(error)}`);
          return of(null);
        }),
        untilDestroyed(this),
      )
      .subscribe((callFlow) => {
        const current = this._tasks$.getValue();
        const idx = current.findIndex((t) => t.unifiedConversationId === task.unifiedConversationId);
        // Ignore a response from a superseded leg (a transfer changed the userSessionId).
        if (idx < 0 || current[idx].userSessionId !== task.userSessionId) {
          return;
        }
        this._callFlowFetchAttempted.add(task.unifiedConversationId);
        this.setCallFlowLoading(task.unifiedConversationId, false);
        if (!callFlow?.length) {
          return;
        }
        this._callFlowLoaded.add(task.unifiedConversationId);
        const updated = [...current];
        updated[idx] = {
          ...updated[idx],
          callFlow,
        };
        this._tasks$.next(updated);
      });
  }

  private setContextParamsLoading(id: string, isLoading: boolean): void {
    const current = new Set(this._contextParamsLoading$.value);
    if (isLoading) {
      current.add(id);
    } else {
      current.delete(id);
    }
    this._contextParamsLoading$.next(current);
  }

  private setCallFlowLoading(id: string, isLoading: boolean): void {
    const current = new Set(this._callFlowLoading$.value);
    if (isLoading) {
      current.add(id);
    } else {
      current.delete(id);
    }
    this._callFlowLoading$.next(current);
  }

  /** States for which the call-flow endpoint is fetched (and therefore a loading spinner is shown). */
  private isCallFlowFetchState(task: ITask): boolean {
    return task.state === TaskState.Ringing || task.state === TaskState.Connected || isAcwState(task.state);
  }

  private buildNamedContextEntries(
    result: TaskParametersResponse | null,
    customContextParameters: Record<string, string> | undefined,
  ): { key: string; value: string; isUrl: boolean }[] {
    if (!result?.conversationParameters) {
      return [];
    }
    const params = customContextParameters ?? {};
    return Object.entries(result.conversationParameters)
      .filter(([key]) => key.startsWith('$(CustomContextParameters.'))
      .filter(([, entry]) => params[entry.parameterId] !== undefined)
      .map(([, entry]) => ({
        key: entry.name,
        value: String(params[entry.parameterId]),
        isUrl: this.isUrl(String(params[entry.parameterId])),
      }));
  }

  private submitCallAttributesForTask(task: ITask): void {
    const stored: TaskAttributes = this.localStorageService.getData(localStorageKeys.callAttributes);

    if (!stored || stored.conversationId !== task.conversationId) {
      return;
    }

    // NimbusOne owns the codes and tags of a Unify call; a draft persisted here must never reach the backend.
    if (isUnifyTask(task)) {
      this.loggingService.Info(
        `AssistantTasksService: discarding call attributes for ${stored.conversationId} - codes and tags of a Unify call are managed in NimbusOne.`,
      );
      this.localStorageService.clearData(localStorageKeys.callAttributes);
      return;
    }

    // Only discard the entry once we know we can act on it, so a failed submission cannot silently
    // destroy what the agent typed.
    if (!this.hub.isConnected$.value || !this.authService.authenticated$.value) {
      this.loggingService.Info(
        `AssistantTasksService: keeping call attributes for ${stored.conversationId} - cannot submit (connected: ${this.hub.isConnected$.value}, authenticated: ${this.authService.authenticated$.value}).`,
      );
      return;
    }

    this.localStorageService.clearData(localStorageKeys.callAttributes);

    const conversationIdNotSet = !stored.conversationId || !stored.unifiedConversationId;
    const primaryCodeList = task.conversationCodes?.primaryCodeList ?? [];
    const secondaryCodeList = task.conversationCodes?.secondaryCodeList ?? [];
    const primaryCodeNotSelected = !stored.primaryCode && primaryCodeList.length > 0;
    const secondaryCodeNotSelected = !stored.secondaryCode && secondaryCodeList.length > 0;
    const emptyAttributes = !stored.primaryCode && !stored.secondaryCode && !stored.tags?.length;

    if (conversationIdNotSet || primaryCodeNotSelected || secondaryCodeNotSelected || emptyAttributes) {
      this.loggingService.Info(`Call attributes not auto-submitted: ${JSON.stringify(stored)}.`);
      return;
    }

    this.loggingService.Info(`Submitting call attributes: ${JSON.stringify(stored)}.`);
    this.apiService
      .setServiceCallAttributes(stored)
      .pipe(
        catchError((err) => {
          this.loggingService.Error(`setServiceCallAttributes error: ${JSON.stringify(err)}`);
          return of(null);
        }),
        take(1),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private mapToConversationInfo(task: ITask): ConversationInfo {
    const info = new ConversationInfo();
    info.id = task.conversationId;
    info.callId = task.unifiedConversationId;
    info.caller = task.caller?.phoneNumber || task.caller?.office365Id;
    info.callerDisplayName = task.caller?.displayName;
    // Populate the caller identity so service-call trigger placeholders
    // $(Caller.TelNumber)/$(Caller.+TelNumber)/$(Caller.Id) resolve (AB#154866).
    info.phoneNumber = task.caller?.phoneNumber;
    info.microsoftTeamsUserId = task.caller?.office365Id;
    info.modality = task.modality;
    info.channel = task.channel;
    info.isServiceCall = task.isServiceCall;
    info.isInternal = task.isInternal;
    info.isExternal = task.isExternal;
    info.isPstn = task.modality === Modality.Audio && !task.isInternal;
    info.isImSession = task.modality === Modality.IM;
    info.isExternalTaskSession = task.modality === Modality.ExternalTask;
    info.isDirectTeamsCall = !task.isServiceCall;
    info.isOutboundServiceCall = task.direction === CallDirection.Outbound;
    info.isServiceTransferredCall = task.startedWithTransfer;
    info.teamInfo = task.teamInfo;
    info.initialServiceName = task.initialServiceName;

    if (task.resolvedParameters) {
      info.resolvedParameters = task.resolvedParameters;
    }

    return info;
  }

  private openServiceContextLinks(task: ITask): void {
    if (!task.isServiceCall || task.isSupervision) {
      return;
    }

    const teamInfo = this.servicesService.getTeamInfoByTeamId(task.teamId);
    if (!teamInfo || (!teamInfo.hasLinksToOpenOnUserInvitation && !task.startedWithTransfer)) {
      return;
    }

    this.apiService
      .getServiceContext(task.caller?.phoneNumber || task.caller?.office365Id, task.teamId)
      .pipe(
        catchError((err) => {
          this.loggingService.Error(`Service context error: ${JSON.stringify(err)}`);
          return of(null);
        }),
        filter((x) => !!x),
        take(1),
        untilDestroyed(this),
      )
      .subscribe((cctx: TeamInfoLinksToOpen) => {
        this.loggingService.Info(`AssistantTasksService: opening ${cctx.linksToOpenOnUserInvitation?.length ?? 0} service context links`);
        cctx.linksToOpenOnUserInvitation?.forEach((url) => {
          const message = new OpenInBrowserMessage();
          message.url = url;
          message.method = 'GET';
          MessageService.SendOpenInBrowser(message);
        });
      });
  }

  private mapSessionToTask(session: IUserSessionLive): ITask {
    const isInternal = session.callerId?.type === CallerType.Guid;
    const isPstn = session.callerId?.type === CallerType.Pstn;
    const isServiceCall = !!session.teamId;

    const caller: CallerInfo = {
      displayName: session.callerParameters?.customer?.calculatedDisplayName || session.callerParameters?.customer?.displayName || null,
      office365Id: isInternal ? session.callerId.id : null,
      phoneNumber: isPstn ? session.callerParameters?.caller?.plusTelNumber || session.callerId.id : null,
    };

    const serviceTeamInfo = session.teamId ? this.servicesService.getTeamInfoByTeamId(session.teamId) : null;
    const teamInfo: TeamInfo | null = serviceTeamInfo ?? null;

    const state = this.mapSessionState(session.state);
    const isAcw = isAcwState(state);

    return {
      unifiedConversationId: session.unifiedConversationId,
      conversationId: session.conversationId,
      userSessionId: session.id,
      state,
      conversationState: session.state as unknown as TaskConversationState,
      since: session.since,
      acceptedAt: session.acceptedAt,
      orderNumber: session.orderNumber,
      modality: session.initialModality as unknown as Modality,
      channel: session.channel as unknown as Channel,
      direction: session.callDirection as unknown as CallDirection,
      taskType: session.taskType,
      caller,
      teamId: session.teamId,
      initialServiceName: session.serviceParameters?.displayName || session.serviceParameters?.name || null,
      teamInfo,
      originType: session.originType,
      isInternal,
      isExternal: !isInternal,
      isServiceCall,
      isDirectCall: false,
      isSupervision: session.isSupervisionSessionInProgress ?? false,
      callFlow: this.buildCallFlow(session),
      contextData: this.buildContextData(session),
      customContextParameters: (session.callerParameters?.customContextParameters as Record<string, string>) ?? undefined,
      startedWithTransfer: session.startedWithTransfer ?? false,
      consultationSession: session.consultationSession ?? null,
      callerHungUpAt: session.callerHungUpAt,
      afterCallWork: isAcw
        ? {
            leftInSeconds: this.getAcwLeftInSeconds(session),
            durationInSeconds: this.parseDurationToSeconds(session.afterCallWorkDuration),
            started: session.since,
            extended: null,
            allowToStop: session.afterCallWorkAllowToStop ?? false,
            allowToExtend: session.afterCallWorkAllowToExtend ?? false,
          }
        : undefined,
    };
  }

  private mapSessionState(state: UserSessionState): TaskState {
    switch (state) {
      case UserSessionState.Selected:
        return TaskState.Ringing;
      case UserSessionState.Parked:
        return TaskState.Parked;
      case UserSessionState.InAfterCallWork:
        return TaskState.InACW;
      case UserSessionState.InExtendedAfterCallWork:
        return TaskState.InExtendedACW;
      // The agent leg of an outbound call with workflow is already accepted while the target is being
      // dialed, so the task is connected - the dial-out is reflected by conversationState, not by TaskState.
      case UserSessionState.DialingOut:
        return TaskState.Connected;
      default:
        return TaskState.Connected;
    }
  }

  private buildCallFlow(session: IUserSessionLive): TaskFlowDetails[] {
    // Caller display name — same priority as portal ac-task-card
    const customer = session.callerParameters?.customer;
    const callerDisplayName =
      customer?.calculatedDisplayName ||
      customer?.displayName ||
      customer?.upn ||
      customer?.email ||
      session.callerParameters?.caller?.telNumber ||
      session.callerId?.id ||
      '';
    const callerEntry = { serviceName: callerDisplayName, participantType: TaskFlowParticipantType.Caller } as TaskFlowDetails;

    const serviceName = session.serviceParameters?.displayName || session.serviceParameters?.name || '';
    if (!serviceName) {
      return [callerEntry];
    }
    const serviceEntry = {
      serviceName,
      servicePstnNumber: session.serviceParameters?.plusTelNumber,
      teamId: session.teamId,
      participantType: TaskFlowParticipantType.IncomingCall,
    } as TaskFlowDetails;

    // Real callFlow payloads read service -> target for outbound calls; match that order so
    // task-flow-tab can trust callFlow's order as-is regardless of whether it came from here or the BE.
    const isOutbound = (session.callDirection as unknown as CallDirection) === CallDirection.Outbound;
    return isOutbound ? [serviceEntry, callerEntry] : [callerEntry, serviceEntry];
  }

  private buildContextData(session: IUserSessionLive): { key: string; value: string; isUrl: boolean }[] {
    const result: { key: string; value: string; isUrl: boolean }[] = [];

    const customer = session.callerParameters?.customer;
    if (customer) {
      for (const { field, key } of AssistantTasksService.CUSTOMER_CONTEXT_FIELDS) {
        const value = customer[field] as string;
        if (value) {
          result.push({ key, value, isUrl: this.isUrl(value) });
        }
      }
    }

    // Live data derives plusTelNumber by prefixing "+", so an empty caller number still yields "+"; check telNumber instead.
    const caller = session.callerParameters?.caller;
    if (caller?.telNumber) {
      result.push({ key: TranslationKeys.AssistantContextTelNumber, value: caller.plusTelNumber, isUrl: false });
    }

    return result;
  }

  private isUrl(value: string): boolean {
    return !!value?.match(/(https?:\/\/(www\.)?.*\..*)/)?.length;
  }

  /**
   * ACW remaining time. `since` is when the current (possibly extended) ACW period started and
   * `afterCallWorkDuration` is the duration granted for it, so on a reload/reconnect the elapsed
   * part has to be subtracted instead of restarting the countdown from the full duration.
   */
  private getAcwLeftInSeconds(session: IUserSessionLive): number {
    const durationInSeconds = this.parseDurationToSeconds(session.afterCallWorkDuration);
    const startedAt = session.since ? moment(session.since) : null;
    if (!startedAt?.isValid()) {
      return durationInSeconds;
    }

    const elapsedInSeconds = moment().diff(startedAt, 'seconds');
    if (elapsedInSeconds <= 0) {
      return durationInSeconds;
    }

    return Math.max(0, durationInSeconds - elapsedInSeconds);
  }

  private parseDurationToSeconds(duration: string): number {
    if (!duration) {
      return 0;
    }
    const parts = duration.split(':');
    if (parts.length === AssistantTasksService.DURATION_PARTS_COUNT) {
      const [hours, minutes, seconds] = parts.map((p) => parseInt(p, 10));
      return hours * AssistantTasksService.SECONDS_PER_HOUR + minutes * AssistantTasksService.SECONDS_PER_MINUTE + seconds;
    }
    return 0;
  }
}
